// Oppie scroll reveal — subtle fade + rise on scroll-into-view.
//
// Robustness contract (design.md §8, CLAUDE.md hard rules §6/§9):
//   - The hidden start-state lives behind `<html class="oppie-anim">`, which
//     this module only sets when JS runs AND motion is allowed. Without JS
//     (crawlers) or under prefers-reduced-motion, `.oppie-reveal` stays fully
//     visible — no hidden content, no SEO/CLS risk.
//   - One-shot: each element reveals once, then is unobserved.
//   - Re-scans on `shopify:section:load` so the Theme Editor works.

const REVEAL_SELECTOR = '.oppie-reveal';
const REVEALED_CLASS = 'is-revealed';
const ANIM_CLASS = 'oppie-anim';

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const revealNow = (element) => {
  element.classList.add(REVEALED_CLASS);
};

const createObserver = () =>
  new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        revealNow(entry.target);
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
  );

const observeWithin = (observer, root) => {
  const scope = root instanceof Element ? root : document;
  const targets = scope.querySelectorAll(REVEAL_SELECTOR);
  targets.forEach((target) => {
    if (target.classList.contains(REVEALED_CLASS)) return;
    observer.observe(target);
  });
};

const revealAllWithin = (root) => {
  const scope = root instanceof Element ? root : document;
  scope.querySelectorAll(REVEAL_SELECTOR).forEach(revealNow);
};

const init = () => {
  // Motion disabled → leave everything visible, never gate the hidden state.
  if (prefersReducedMotion()) return;

  document.documentElement.classList.add(ANIM_CLASS);

  // No IntersectionObserver (old browser) → reveal immediately so nothing
  // stays stuck hidden once oppie-anim is active.
  if (!('IntersectionObserver' in window)) {
    revealAllWithin(document);
    return;
  }

  const observer = createObserver();
  observeWithin(observer, document);

  // Theme Editor: re-scan freshly injected sections.
  document.addEventListener('shopify:section:load', (event) => {
    observeWithin(observer, event.target);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
